'use client';

import { Button, Card } from '@instamenu/ui-system-next';
import { breakpoints } from '@instamenu/ui-vanilla-extract';
import { useRouter } from 'next/navigation';
import { useMediaQuery } from 'react-responsive';
import { Clock } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { DeliveryStatus } from '@instamenu/data-access';
import { useOrderPolling, type Order } from '@instamenu/context-checkout';
import { dateFormats, formatDate } from '@instamenu/formatters';
import { makePath } from '../utils';

import { DeliveryTracker, getDeliveryError } from '../delivery-tracker/delivery-tracker';
import { CompleteLoyalty } from '../loyalty-status/complete-loyalty';
import { EnrollmentCard3D } from '../loyalty-status/enrollment-card';
import {
  ActionRow,
  AddressBlock,
  OrderCompleteHero,
  OrderCompleteLayout,
  OrderCompleteStack,
  OrderCompleteTwoColumn,
  StatusPill,
} from './components';
import { getDeliveryPillContent } from './status-pill-content';

interface DeliveryOrderProps {
  order: Order;
}

const formatEta = (eta: Parameters<typeof formatDate>[0]): string | null => {
  if (!eta) return null;
  try {
    return formatDate(eta, dateFormats.timeOnly);
  } catch {
    return null;
  }
};

const Actions = ({ order, router }: { order: Order; router: ReturnType<typeof useRouter> }) => (
  <ActionRow>
    <Button
      css={{ flex: 1 }}
      onClick={() => router.push(`${process.env.NEXT_PUBLIC_API_ENDPOINT}/orders/${order.id}`)}
      data-id="order-complete-view-receipt"
    >
      View Receipt
    </Button>
    <Button
      variant="secondary"
      css={{ flex: 1 }}
      onClick={() =>
        router.push(
          makePath({
            restaurantId: order.restaurant.id,
            menuId: order.restaurant.menuId,
          }),
        )
      }
      data-id="order-complete-order-more"
    >
      Order More
    </Button>
  </ActionRow>
);

export const DeliveryOrder = observer(({ order }: DeliveryOrderProps) => {
  const isDesktop = useMediaQuery({ query: breakpoints.tablet });
  const router = useRouter();
  const hasError = getDeliveryError(order);
  const isDelivered = order.delivery?.status === DeliveryStatus.Delivered;

  useOrderPolling(order, { pause: isDelivered || hasError });

  const greeting = `Thanks${order.user?.firstName ? `, ${order.user.firstName}` : ''}!`;
  const eta = formatEta(order.delivery?.dropoffEtaAt);

  // Reflect real order/delivery state (re-renders live via `useOrderPolling`
  // above). When the order is canceled or hits a delivery problem, the helper
  // returns an error-tone pill instead of a generic "On its way".
  const pillContent = getDeliveryPillContent(order);
  const statusPill = (
    <StatusPill
      tone={pillContent.tone}
      icon={pillContent.icon}
      data-id="order-complete-status-pill"
    >
      {pillContent.label}
    </StatusPill>
  );

  const addressBlock = order.deliveryAddress ? (
    <AddressBlock
      label="Delivering to"
      primary={order.deliveryAddress.line1}
      secondary={
        <>
          {order.deliveryAddress.line2}
          {order.deliveryAddress.specialInstructions ? (
            <>
              <br />
              {order.deliveryAddress.specialInstructions}
            </>
          ) : null}
        </>
      }
      data-id="order-complete-delivery-address"
    />
  ) : null;

  const etaPill = eta ? (
    <StatusPill
      tone="eta"
      icon={<Clock size={12} strokeWidth={2.5} />}
      data-id="order-complete-eta-pill"
    >
      Estimated arrival: {eta}
    </StatusPill>
  ) : null;

  const tracker = !order.scheduledAt ? (
    <Card css={{ width: '100%', flex: 1 }} loadingAnimation={!hasError && !isDelivered}>
      <DeliveryTracker order={order} />
    </Card>
  ) : null;

  const loyalty = (
    <>
      <CompleteLoyalty />
      <EnrollmentCard3D />
    </>
  );

  const hero = <OrderCompleteHero pill={statusPill} greeting={greeting} />;

  const primaryColumn = (
    <OrderCompleteStack>
      {addressBlock}
      {etaPill ? <div data-id="order-complete-eta-row">{etaPill}</div> : null}
      <Actions order={order} router={router} />
      {loyalty}
    </OrderCompleteStack>
  );

  return (
    <OrderCompleteLayout hero={hero}>
      {isDesktop && tracker ? (
        <OrderCompleteTwoColumn left={primaryColumn} right={tracker} />
      ) : (
        <OrderCompleteStack>
          {primaryColumn}
          {tracker}
        </OrderCompleteStack>
      )}
    </OrderCompleteLayout>
  );
});
